import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import { graphql } from "gql.tada";
import { useParams } from "react-router-dom";
import * as yup from "yup";
import {
  EventReservationStatusAfterPayment,
  EventReservationStatusAfterRegistration,
  SeatAssignmentMode,
} from "../../../../../gql/graphql";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";
import { FormSkeleton } from "../../../components/SkeletonLoader";

const EVENT_REGISTRATION_QUERY = graphql(`
  query eventRegistration($id: ID!) {
    event(id: $id) {
      startRegistrationsAt
      endRegistrationsAt
      configuration {
        sessionDuration
        groupConfiguration {
          maxSize
          minSize
        }
        reservationStatusAfterPayment
        reservationStatusAfterRegistration
        maxTicketsPerReservation
        allowForNewSessionAfterTimeout
        allowForMoreTicketsThanGroupMembers
        seatAssignmentMode
      }
    }
  }
`);

const UPDATE_EVENT_REGISTRATION_MUTATION = graphql(`
  mutation eventUpdateRegistration(
    $id: ID!
    $configuration: EventConfigurationInput!
    $startRegistrationsAt: DateTime!
    $endRegistrationsAt: DateTime!
  ) {
    eventUpdate(
      id: $id
      input: {
        startRegistrationsAt: $startRegistrationsAt
        endRegistrationsAt: $endRegistrationsAt
        configuration: $configuration
      }
    ) {
      id
    }
  }
`);

interface RegistrationFormProps {
  eventId: string;
  event: any;
}

const RegistrationForm = ({ eventId, event }: RegistrationFormProps) => {
  const [updateEvent] = useMutation<any>(UPDATE_EVENT_REGISTRATION_MUTATION);

  const [allowMore, setAllowMore] = useState<boolean>(
    event?.configuration?.allowForMoreTicketsThanGroupMembers === true,
  );
  const [maxGroupSize, setMaxGroupSize] = useState<number>(
    event?.configuration?.groupConfiguration?.maxSize ?? 1,
  );

  return (
    <CreationForm
      schemaDefinition={{
        startRegistrationsAt: {
          type: "INPUT",
          label: "Registrations open at",
          yupConfig: yup.date().required(),
          inputType: "date",
          placeholder: "Enter the event reservations open date",
          defaultValue: event?.startRegistrationsAt,
        },
        endRegistrationsAt: {
          type: "INPUT",
          label: "Registrations closes at",
          yupConfig: yup.date().required(),
          inputType: "date",
          placeholder: "Enter the event reservations ending date",
          defaultValue: event?.endRegistrationsAt,
        },
        sessionDuration: {
          type: "INPUT",
          label: "Session duration",
          yupConfig: yup.number().required(),
          inputType: "number",
          placeholder: "Enter the event session duration",
          info: "The duration of a reservation session in seconds, default is 900 (15 minutes)",
          defaultValue: event?.configuration?.sessionDuration,
        },
        minGroupSize: {
          type: "INPUT",
          label: "Min group size",
          yupConfig: yup.number().required(),
          inputType: "number",
          placeholder: "Enter the event min group size",
          info: "The minimum size of a group, default is 1",
          defaultValue: event?.configuration?.groupConfiguration?.minSize,
        },
        maxGroupSize: {
          type: "INPUT",
          label: "Max group size",
          yupConfig: yup.number().required(),
          inputType: "number",
          placeholder: "Enter the event max group size",
          info: "The maximum size of a group, default is 1",
          defaultValue: event?.configuration?.groupConfiguration?.maxSize,
          onChange: (value) => setMaxGroupSize(Number(value) || 0),
        },
        reservationStatusAfterPayment: {
          type: "SELECT",
          label: "Reservation status after payment",
          yupConfig: yup.string().required(),
          options: [
            {
              label: "Confirmed",
              value: EventReservationStatusAfterPayment.Confirmed,
            },
            {
              label: "Awaiting for presence confirmation",
              value:
                EventReservationStatusAfterPayment.PresenceConfirmationPending,
            },
            {
              label: "Registered",
              value: EventReservationStatusAfterPayment.Registered,
            },
          ],
          defaultValue: event?.configuration?.reservationStatusAfterPayment,
        },
        reservationStatusAfterRegistration: {
          type: "SELECT",
          label: "Reservation status after registration",
          yupConfig: yup.string().required(),
          options: [
            {
              label:
                "Registered to event (useful to allow user to create a group and invite other users)",
              value: EventReservationStatusAfterRegistration.None,
            },
            {
              label: "Start session to buy tickets",
              value: EventReservationStatusAfterRegistration.Pending,
            },
            {
              label: "Requires admin approval",
              value:
                EventReservationStatusAfterRegistration.RequiresAdminApproval,
            },
          ],
          defaultValue:
            event?.configuration?.reservationStatusAfterRegistration,
        },
        allowForMoreTicketsThanGroupMembers: {
          type: "CHECKBOX",
          label: "Allow to reserve more tickets than group members",
          yupConfig: yup.boolean(),
          defaultValue:
            event?.configuration?.allowForMoreTicketsThanGroupMembers === true,
          onChange: (checked) => setAllowMore(checked),
        },
        maxTicketsPerReservation: {
          type: "INPUT",
          label: "Max tickets per reservation",
          yupConfig: yup.number(),
          inputType: "number",
          defaultValue: event?.configuration?.maxTicketsPerReservation,
          disabled: !allowMore,
          value: allowMore ? undefined : maxGroupSize,
          info: allowMore
            ? "Must be at least the maximum group size"
            : "Automatically set to the maximum group size while reserving more tickets than group members is disabled",
        },
        allowForNewSessionAfterTimeout: {
          type: "CHECKBOX",
          label: "Allow new session after timeout",
          yupConfig: yup.boolean(),
          defaultValue:
            event?.configuration?.allowForNewSessionAfterTimeout === true,
        },
        seatAssignmentMode: {
          type: "SELECT",
          label: "Seat selection mode",
          yupConfig: yup.string().required(),
          defaultValue:
            event?.configuration?.seatAssignmentMode ??
            SeatAssignmentMode.Optional,
          options: [
            {
              label: "Seat selection is optional",
              value: SeatAssignmentMode.Optional,
            },
            {
              label: "Seat selection is required",
              value: SeatAssignmentMode.Required,
            },
            {
              label: "Seat selection is disabled",
              value: SeatAssignmentMode.None,
            },
          ],
        },
      }}
      onSubmit={async (values) => {
        const maxTicketsPerReservation = allowMore
          ? values.maxTicketsPerReservation
          : values.maxGroupSize;
        await updateEvent({
          variables: {
            id: eventId,
            startRegistrationsAt: values.startRegistrationsAt,
            endRegistrationsAt: values.endRegistrationsAt,
            configuration: {
              reservationStatusAfterRegistration:
                values.reservationStatusAfterRegistration,
              reservationStatusAfterPayment:
                values.reservationStatusAfterPayment,
              sessionDuration: values.sessionDuration,
              maxTicketsPerReservation,
              allowForNewSessionAfterTimeout:
                values.allowForNewSessionAfterTimeout,
              allowForMoreTicketsThanGroupMembers: allowMore,
              seatAssignmentMode: values.seatAssignmentMode,
              groupConfiguration: {
                minSize: values.minGroupSize,
                maxSize: values.maxGroupSize,
              },
            },
          },
        }).then((data) => {
          if (data?.error) {
            throw new Error(data.error.message);
          }
        });
        notifications.show({
          title: "Event updated",
          message: "Registration settings have been updated",
          color: "green",
          autoClose: 3000,
        });
      }}
    />
  );
};

export const RegistrationSettings = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const eventId = useParams().eventId!;
  const { loading, data } = useQuery<any>(EVENT_REGISTRATION_QUERY, {
    variables: { id: eventId },
    skip: !eventId,
  });

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Registration Settings"
        showBackButton
        showBreadcrumbs={false}
      />
      {loading || !data?.event ? (
        <FormSkeleton fields={8} />
      ) : (
        <RegistrationForm eventId={eventId} event={data.event} />
      )}
    </div>
  );
};
